import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Activity, Bookmark, Factory, Globe, ListChecks, ThumbsUp } from 'lucide-react';

import type { TransformedPointOfSaleReport } from '@ballerine/common';

import type { PreviousReportsLinks } from '../../../report-shared/components/PreviousReportsButton/PreviousReportsButton';
import {
  EcosystemSection,
  HeaderSection,
  MerchantDetailsSection,
  OperationalActivitySection,
  ReferencesSection,
  RiskAnalysisSection,
  SocialMediaSection,
  TagsSection,
} from '../../sections';

type PointOfSaleReportSection = {
  id: string;
  title: string;
  description?: string;
  Icon?: LucideIcon;
  Component: ReactNode;
  hasViolations?: boolean;
  isPremium?: boolean;
  excludeFromObserver?: boolean;
  isHidden: boolean;
};

export const usePointOfSaleReportSections = (
  report: TransformedPointOfSaleReport & {
    monitoringStatus?: string | null;
    changesSummary?: string | null;
    isAlert?: boolean | null;
    comparedToReportId?: string | null;
  },
  googleMapsApiKey?: string,
  FeedbackComponent?: React.ComponentType<{ reportId: string }>,
  Link?: React.ComponentType<{
    to: string;
    children: React.ReactNode;
    className?: string;
  }>,
  reportType?: string,
  previousReportsLinks?: PreviousReportsLinks | null,
) => {
  const {
    providedDetails,
    ecosystem,
    socialMedia,
    synthesizerResult,
    matchReason,
    merchantDetails,
    operationalProof,
    structuredTags,
  } = report.data;
  const {
    references,
    riskTypes,
    riskLevel,
    riskAnalysis,
    tags: synthesizerTags,
  } = synthesizerResult ?? {};
  const { mcc, lineOfBusiness, lineOfBusinessReasoning } = operationalProof ?? {};
  const { facebook, instagram, twitter, linkedin, tiktok } = socialMedia ?? {};

  const isMonitoringReport =
    reportType === 'ONGOING_POINT_OF_SALE_REPORT' ||
    report.reportType === 'ONGOING_POINT_OF_SALE_REPORT';
  const ongoingMonitoringSummary = (report as any).changesSummary ?? null;
  const isAlert = (report as any).isAlert ?? false;

  const tags = structuredTags?.length ? structuredTags : synthesizerTags;

  const sections = useMemo(() => {
    const allSections = [
      {
        id: 'header',
        Icon: Globe,
        excludeFromObserver: true,
        isHidden: false,
        Component: (
          <HeaderSection
            reportId={report.id}
            riskLevel={riskLevel}
            riskTypes={riskTypes}
            lineOfBusiness={lineOfBusiness}
            mcc={mcc}
            isMonitoringReport={isMonitoringReport}
            isAlert={isAlert}
            FeedbackComponent={FeedbackComponent}
            merchantDetails={merchantDetails}
            googleMapsApiKey={googleMapsApiKey}
            reportType={reportType}
          />
        ),
      },
      {
        id: 'tags',
        isHidden: !tags?.length,
        Icon: ListChecks,
        excludeFromObserver: true,
        Component: <TagsSection tags={tags} />,
      },
      {
        id: 'risk-analysis',
        title: 'Risk Analysis',
        isHidden: riskAnalysis === undefined && !ongoingMonitoringSummary,
        Icon: ListChecks,
        Component: (
          <RiskAnalysisSection
            summary={riskAnalysis}
            createdAt={
              typeof report.createdAt === 'string' ? new Date(report.createdAt) : report.createdAt
            }
            ongoingMonitoringSummary={ongoingMonitoringSummary}
            homepageScreenshotUrl={null}
            previousReportsLinks={previousReportsLinks}
            isAlert={isAlert}
            isMonitoringReport={isMonitoringReport}
          />
        ),
      },
      {
        id: 'merchant-details',
        title: 'Operating Company Details',
        isHidden: !merchantDetails,
        Icon: Factory,
        Component: (
          <MerchantDetailsSection
            merchantDetails={merchantDetails}
            providedDetails={providedDetails}
            matchReason={matchReason}
            googleMapsApiKey={googleMapsApiKey}
          />
        ),
      },
      {
        id: 'operational-analysis',
        title: 'Operational Analysis',
        isHidden: !lineOfBusiness && !mcc,
        Icon: Activity,
        Component: (
          <OperationalActivitySection
            lineOfBusiness={lineOfBusiness}
            lineOfBusinessReasoning={lineOfBusinessReasoning}
            mcc={mcc}
          />
        ),
      },
      {
        id: 'ecosystem',
        title: 'Ecosystem',
        isHidden: ecosystem === undefined,
        Icon: Globe,
        Component: <EcosystemSection ecosystem={ecosystem} />,
      },
      {
        id: 'social-media',
        title: 'Social Media',
        isHidden: socialMedia === undefined,
        Icon: ThumbsUp,
        Component: (
          <SocialMediaSection
            facebookPage={facebook}
            instagramPage={instagram}
            twitterPage={twitter}
            linkedinPage={linkedin}
            tiktokPage={tiktok}
          />
        ),
      },
      {
        id: 'references',
        title: 'References',
        isHidden: references === undefined,
        Icon: Bookmark,
        Component: <ReferencesSection references={references} />,
      },
    ] as PointOfSaleReportSection[];

    return allSections.filter(section => !section.isHidden);
  }, [
    report.data,
    report.id,
    report.createdAt,
    report.reportType,
    reportType,
    googleMapsApiKey,
    FeedbackComponent,
    Link,
    isMonitoringReport,
    ongoingMonitoringSummary,
    isAlert,
    previousReportsLinks,
  ]);

  return { sections };
};
